import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * An answer as Markdown with GFM tables, lists and strike-through, sized for the chat. Raw HTML in the answer is not
 * rendered (react-markdown's default) and links open in a new tab.
 */
export function CopilotMarkdown({ content }: { content: string }) {
  return (
    <Box
      sx={{
        fontSize: '0.875rem',
        lineHeight: 1.6,
        wordBreak: 'break-word',
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        '& p': { m: 0, mb: 1 },
        '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
        '& li': { mb: 0.375 },
        '& li > p': { mb: 0 },
        '& strong': { fontWeight: 700 },
        '& h1, & h2, & h3, & h4': { fontSize: '0.9rem', fontWeight: 700, mt: 1.5, mb: 0.5 },
        '& hr': { my: 1, border: 0, borderTop: '1px solid', borderColor: 'divider' },
        '& blockquote': {
          m: 0, mb: 1, pl: 1.25, borderLeft: '3px solid', borderColor: 'divider', color: 'text.secondary',
        },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.85em', bgcolor: 'action.selected', borderRadius: 0.5, px: 0.5, py: 0.125,
        },
        '& pre': {
          bgcolor: 'action.selected', borderRadius: 1, p: 1, mb: 1, overflowX: 'auto',
          '& code': { bgcolor: 'transparent', p: 0 },
        },
        '& a': { color: 'primary.main' },
        // Wide tables scroll inside the bubble instead of pushing the panel wider.
        '& .copilot-table': { overflowX: 'auto', mb: 1, maxWidth: '100%' },
        '& table': { borderCollapse: 'collapse', fontSize: '0.8125rem', minWidth: '100%' },
        '& th, & td': {
          border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left', verticalAlign: 'top',
        },
        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, title, children }) => (
            <a href={href} title={title} target="_blank" rel="noopener noreferrer">{children}</a>
          ),
          table: ({ children }) => (
            <div className="copilot-table"><table>{children}</table></div>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}
